/**
 * ExportViewScreen — the "set the export view" step. The user positions a live
 * map (basemap + overlay + zone outlines); that camera is then reused to render
 * each selected design with the existing SummaryPdf report, appended into ONE
 * multi-page PDF. The old report captures this live map (captureMapImage), so
 * the angle, the overlay and the outlines all carry into the export.
 */
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import type { Editor } from "src/types/Editor";
import type { GeneratedItem } from "src/types/GeneratedItem";
import type { World } from "src/types/World";
import { Selection } from "src/types/Selection";
import { MapState } from "src/types/MapState";
import { useApi } from "src/contexts/api.context";
import { useToaster } from "src/contexts/toaster.context";
import { buildSummaryPdfData } from "src/utils/generateAndDownloadSummaryPdf";
import type { SummaryPdfData } from "src/components/SummaryPdf/buildSummaryData";
import { ToastType } from "../Toast";
import { DesignMap } from "./DesignMap";
import { OverlayControls } from "./OverlayControls";
import { Icon, ACCENT } from "./Icon";

const icons = {
  back: <path d="M15 6l-6 6 6 6" />,
  layers: <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5" />,
  download: <path d="M12 16V4M6 10l6 6 6-6M4 20h16" />,
};

const sanitize = (name: string) => name.replace(/[/\\:*?"<>|]/g, "_").trim() || "tessa-project";
const stamp = () => new Date().toISOString().slice(0, 10);

export const ExportViewScreen = observer(function ExportViewScreen({
  editor,
  items,
  projectId,
  projectName,
  reportType,
  onClose,
}: {
  editor: Editor;
  items: GeneratedItem[];
  projectId: string;
  projectName: string;
  reportType: "construction" | "financial";
  onClose: () => void;
}) {
  const api = useApi();
  const toaster = useToaster();
  const [mapState, setMapState] = useState<MapState | null>(null);
  const [world, setWorld] = useState<World | null>(null);
  const [selection] = useState(() => new Selection(null));
  const [ovOpen, setOvOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sitePlanMode, setSitePlanMode] = useState(false);

  // Load the first design so the user has something to position against.
  useEffect(() => {
    let cancelled = false;
    const base = editor.world;
    if (!items[0] || !base) return;
    editor.loader
      .loadFromDesignGenerator(items[0], base)
      .then((dw) => {
        if (cancelled) return;
        selection.setWorld(dw);
        setWorld(dw);
        setMapState(new MapState(dw));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editor, items, selection]);

  // Swap the displayed design + give the map a beat to re-render the buildings
  // before we capture. The camera persists on the shared map state.
  const show = async (dw: World) => {
    selection.setWorld(dw);
    mapState?.setWorld(dw);
    setWorld(dw);
    await new Promise((resolve) => setTimeout(resolve, 900));
  };

  // Shared site plan: force the camera top-down (north-up) and hide the
  // buildings, leaving basemap + overlay + zone outlines — like the brief map.
  // Restores the user's camera afterwards.
  const captureSitePlan = async (): Promise<string | null> => {
    if (!mapState) return null;
    const pitch = mapState.getMapPitch();
    const bearing = mapState.getMapBearing();
    mapState.setCamera({ pitch: 0, bearing: 0 });
    setSitePlanMode(true);
    await new Promise((r) => setTimeout(r, 1100));
    const url = await mapState.captureMapImage({ hideBuildings: true });
    setSitePlanMode(false);
    mapState.setCamera({ pitch, bearing });
    await new Promise((r) => setTimeout(r, 700));
    return url;
  };

  const generate = async () => {
    if (busy || !mapState) return;
    setBusy(true);
    setProgress(0);
    const base = editor.world;
    try {
      // One shared, top-down, buildings-hidden site plan (the site is the same
      // across designs); each design keeps its own on-map view at the user's angle.
      const sitePlanUrl = await captureSitePlan();
      // Build each design's data while it's framed on the live map, then append
      // them all into ONE document.
      const datas: SummaryPdfData[] = [];
      for (let i = 0; i < items.length; i++) {
        if (!base) break;
        const dw = await editor.loader.loadFromDesignGenerator(items[i], base);
        await show(dw);
        datas.push(
          await buildSummaryPdfData({
            world: dw,
            projectId,
            projectName,
            api,
            mapState,
            sitePlanOverride: sitePlanUrl,
            // Only tag a per-design label when there's more than one design.
            metadata: items.length > 1 ? { name: `Design ${i + 1}` } : undefined,
          })
        );
        setProgress(i + 1);
      }
      if (datas.length === 0) return;

      const [{ pdf }, { SummaryPdfMultiDocument }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("src/components/SummaryPdf/SummaryPdfDocument"),
      ]);
      const blob = await pdf(
        <SummaryPdfMultiDocument designs={datas} kind={reportType} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sanitize(projectName)}-${reportType}-${stamp()}.pdf`;
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      console.error("report generation failed:", e);
      toaster.show({
        text: "Couldn't generate the report: " + (e instanceof Error ? e.message : String(e)),
        type: ToastType.DANGER,
        autoHide: false,
        id: crypto.randomUUID(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#f3f4f6]">
      <div className="h-14 shrink-0 border-b border-[#d1d5db] bg-white flex items-center px-3 gap-3">
        <button
          onClick={onClose}
          disabled={busy}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-[12px] text-[#4b5563] hover:bg-[#f3f4f6] disabled:opacity-50"
        >
          <Icon d={icons.back} size={16} /> Back
        </button>
        <div className="pl-3 border-l border-[#e5e7eb]">
          <div className="text-[13px] font-semibold text-[#1f2937]">Set the export view</div>
          <div className="text-[10px] text-[#9ca3af]">
            This is how your {items.length} design{items.length === 1 ? "" : "s"} appear in the
            report.
          </div>
        </div>

        <div className="grow" />
        {mapState && (
          <div className="relative">
            <button
              onClick={() => setOvOpen((v) => !v)}
              className={
                "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-medium border transition-colors " +
                (mapState.stencil.loadedCatalogId
                  ? "text-[#7c5cff] border-[#c9bcff] bg-[rgba(124,92,255,0.06)]"
                  : "text-[#6b7280] border-[#e5e7eb] hover:bg-[#f9fafb]")
              }
            >
              <Icon d={icons.layers} size={14} /> Overlay
            </button>
            {ovOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setOvOpen(false)} />
                <div className="absolute right-0 top-10 z-20 w-[290px] rounded-lg border border-[#e5e7eb] bg-white shadow-lg p-2">
                  <OverlayControls mapState={mapState} world={world} />
                </div>
              </>
            )}
          </div>
        )}
        <button
          onClick={() => void generate()}
          disabled={busy || !mapState}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-white text-[12px] font-semibold disabled:opacity-50"
          style={{ background: ACCENT }}
        >
          <Icon d={icons.download} size={15} />
          {busy
            ? `Generating ${progress}/${items.length}…`
            : `Generate ${items.length} report${items.length === 1 ? "" : "s"}`}
        </button>
      </div>

      <div className="grow min-h-0 p-3 relative">
        <div className="w-full h-full rounded-xl border border-[#e5e7eb] bg-white shadow-sm overflow-hidden relative">
          {world && mapState ? (
            <DesignMap
              world={world}
              mapState={mapState}
              selection={selection}
              hideDesign={sitePlanMode}
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-[12px] text-[#9ca3af]">
              Loading site…
            </div>
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 text-[11px] text-[#4b5563] bg-white/95 border border-[#d1d5db] rounded-md px-3 py-1.5 shadow-sm pointer-events-none">
            Orbit and zoom to frame the site — drag to rotate, scroll to zoom. Add an overlay to
            include it.
          </div>
        </div>
      </div>
    </div>
  );
});
